import {
    DollarCircleOutlined,
    RetweetOutlined,
    UserSwitchOutlined,
} from "@ant-design/icons";
import { Flex, MenuProps } from "antd";
import { FC, useCallback, useMemo } from "react";
import { AnyTask, AssignmentType, Point, TaskRoute } from "tm-shared/types";

import { ASSIGNMENT_VOCABULARY } from "@/constants/ui/assignment";
import { PointActionsBar, pointToCopy } from "@/domains/point";
import { useCreateTask } from "@/domains/tasks";
import { useQueueInfo } from "@/hooks";
import {
    useAppDispatch,
    useAppSelector,
    useOfflineModeSelector,
} from "@/hooks/redux";
import { addModalToQueue } from "@/store/slices/ui";
import { ModalType } from "@/types/modals";

import { useWorkPlan } from "../../../../WorkPlan.context";
import { PointTasks } from "../PointTasks";
import { sortStartVisitFirstTasks } from "./PointBody.utils";
import { StyledAdditionalSaleButton, StyledMenuButton } from "./Styled";

type MenuItemWithClose = NonNullable<MenuProps["items"]>[number] & {
    closeDropdown?: boolean;
};

interface PointBodyProps {
    item: Point;
    onOpenTasksInfoModal: (params: { task: AnyTask; point: Point }) => void;
}

export const PointBody: FC<PointBodyProps> = ({
    item: point,
    onOpenTasksInfoModal,
}) => {
    const pointId = point.id;
    const { capabilities } = useWorkPlan();

    const pointTasksMap = useAppSelector((s) => s.work.tasks[pointId]);
    const offlineMode = useOfflineModeSelector();

    const dispatch = useAppDispatch();

    const { isRunning: isQueueRunning } = useQueueInfo();

    const pointTasks = useMemo(() => {
        if (!pointTasksMap) return [];

        return sortStartVisitFirstTasks(Object.values(pointTasksMap));
    }, [pointTasksMap]);

    const {
        createAdditionalSale,
        createTakeDebtTask,
        createCustomTask,
        createRejectTask,
        creating: creatingTask,
    } = useCreateTask({ pointId, pointTasks });

    const incompleteRoutes = useMemo(() => {
        const set = new Set<TaskRoute>();
        for (const t of pointTasks) {
            if (!t.completed) set.add(t.context.route);
        }
        return set;
    }, [pointTasks]);

    const canCreateTask = useCallback(
        (route: TaskRoute, options?: { ignoreCreatingTask?: boolean }) => {
            const hasUncompletedTask = incompleteRoutes.has(route);
            const hasIncompleteStartVisit = incompleteRoutes.has(
                TaskRoute.StartVisit,
            );
            const hasIncompleteGeoLocation = incompleteRoutes.has(
                TaskRoute.SetGeoLocation,
            );

            const creatingAnyTask = !!creatingTask;

            const blockedByStartVisit =
                hasIncompleteStartVisit && route !== TaskRoute.StartVisit;

            const blockedByCreatingTask =
                !options?.ignoreCreatingTask && creatingAnyTask;

            return (
                !isQueueRunning &&
                !hasUncompletedTask &&
                !blockedByCreatingTask &&
                !blockedByStartVisit &&
                !hasIncompleteGeoLocation
            );
        },
        [creatingTask, incompleteRoutes, isQueueRunning],
    );

    const createCustomTaskForAgent = useCallback(async () => {
        dispatch(
            addModalToQueue({
                type: ModalType.CreateCustomTask,
                id: ModalType.CreateCustomTask,
                payload: {
                    point,
                    images: [],
                },
            }),
        );
    }, [dispatch, point]);

    const canCreateAdditionalSale = useMemo(() => {
        const canCreate = canCreateTask(TaskRoute.SaleAdditional, {
            ignoreCreatingTask: true,
        });

        return canCreate && capabilities.canWorkWithSalesTasks;
    }, [canCreateTask, capabilities.canWorkWithSalesTasks]);

    const getDropdownMenu = useCallback((): MenuProps => {
        const menuProps: { items: MenuItemWithClose[] } = {
            items: [],
        };

        if (capabilities.canWorkWithSalesTasks) {
            menuProps.items.push({
                key: "1",
                label: (
                    <StyledMenuButton
                        type="text"
                        icon={<RetweetOutlined />}
                        loading={creatingTask === TaskRoute.RejectProducts}
                    >
                        Повернення товарів
                    </StyledMenuButton>
                ),
                onClick: createRejectTask,
                disabled: !canCreateTask(TaskRoute.RejectProducts),
            });

            if (point.hasDebt) {
                menuProps.items.push({
                    key: "2",
                    label: (
                        <StyledMenuButton
                            type="text"
                            icon={<DollarCircleOutlined />}
                            loading={creatingTask === TaskRoute.TakeDebt}
                        >
                            Забрати борг
                        </StyledMenuButton>
                    ),
                    onClick: createTakeDebtTask,
                    disabled: !canCreateTask(TaskRoute.TakeDebt),
                });
            }
        }

        const PricesDistributionIcon =
            ASSIGNMENT_VOCABULARY[AssignmentType.PricesDistribution].icon;

        menuProps.items.push({
            key: "3",
            label: (
                <StyledMenuButton
                    type="text"
                    icon={<PricesDistributionIcon />}
                    loading={creatingTask === TaskRoute.CustomTask}
                >
                    {
                        ASSIGNMENT_VOCABULARY[AssignmentType.PricesDistribution]
                            .label
                    }
                </StyledMenuButton>
            ),
            onClick: createCustomTask,
            disabled: !canCreateTask(TaskRoute.CustomTask),
        });

        if (capabilities.canCreateCustomTaskForAgent) {
            menuProps.items.push({
                key: "4",
                label: (
                    <StyledMenuButton type="text" icon={<UserSwitchOutlined />}>
                        Створення доручення
                    </StyledMenuButton>
                ),
                onClick: createCustomTaskForAgent,
                disabled: offlineMode,
                closeDropdown: true,
            });
        }

        return menuProps;
    }, [
        capabilities.canWorkWithSalesTasks,
        capabilities.canCreateCustomTaskForAgent,
        creatingTask,
        createCustomTask,
        canCreateTask,
        createRejectTask,
        point.hasDebt,
        createTakeDebtTask,
        createCustomTaskForAgent,
        offlineMode,
    ]);

    const menu = pointTasks?.length > 0 ? getDropdownMenu() : undefined;

    return (
        <Flex vertical gap={16} align="flex-start">
            <PointActionsBar
                pointId={point.id}
                pointName={point.name ?? point.market.contragent.name}
                objToCopy={pointToCopy(point)}
                geo={point.market.building.geo}
                address={point.market.building.address.name}
                menu={menu}
            />

            <PointTasks
                tasks={pointTasks}
                point={point}
                incompleteRoutes={incompleteRoutes}
                onOpenTasksInfoModal={onOpenTasksInfoModal}
            />

            {canCreateAdditionalSale && (
                <StyledAdditionalSaleButton
                    loading={creatingTask === TaskRoute.SaleAdditional}
                    type="text"
                    onClick={createAdditionalSale}
                    disabled={!!creatingTask}
                >
                    Додатковий продаж
                </StyledAdditionalSaleButton>
            )}
        </Flex>
    );
};
